<script lang="ts">
  import SectionHeading from '$lib/components/ui/section-heading.svelte';

  /**
   * The outside of every standard content section.
   *
   * One place that owns the background, the vertical rhythm, the container
   * width and the heading block — so a section type only has to decide what
   * goes *inside*. The reason the old pages drifted is that each section
   * spelled its own wrapper: `py-24 sm:py-32` written out 57 times, twelve
   * more sections using `py-20` or `py-16`, five different inner widths, and
   * twenty heading blocks building their own `text-center mb-16`.
   *
   * `tone` is the only background choice. Three values, because the page
   * rhythm is light / muted / dark alternating, and a section that wants a
   * fourth surface is a section that wants a new tone added here rather than
   * a one-off class at the call site.
   *
   * Sections render their heading through this rather than themselves, so the
   * eyebrow, the heading size, the lead measure and the gap below the block
   * are the same everywhere by construction.
   */
  type Tone = 'light' | 'muted' | 'dark';

  let {
    tone = 'light',
    eyebrow = '',
    heading = '',
    lead = '',
    /** `wide` is the default shell; `mid` and `prose` narrow the content. */
    width = 'wide',
    /** Set when the section draws its own heading, e.g. a split. */
    bare = false,
    align = 'center',
    id = '',
    children
  }: {
    tone?: Tone;
    eyebrow?: string;
    heading?: string;
    lead?: string;
    width?: 'wide' | 'mid' | 'prose';
    bare?: boolean;
    /** Forwarded to `SectionHeading`. Left suits a page whose sections are
     * all ranged left — a centred opener on one reads as a stray. */
    align?: 'center' | 'left';
    id?: string;
    children: import('svelte').Snippet;
  } = $props();

  const bg = $derived({ light: 'bg-surface', muted: 'bg-surface-muted', dark: 'hero-brand' }[tone]);
  const inner = $derived({ wide: '', mid: 'mx-auto max-w-4xl', prose: 'mx-auto max-w-3xl' }[width]);
</script>

<section class="{bg} section-y" {...id ? { id } : {}}>
  <div class="mx-auto max-w-7xl px-6 lg:px-8">
    {#if heading && !bare}
      <SectionHeading {eyebrow} {heading} {lead} {align} inverse={tone === 'dark'} />
    {/if}
    <div class={inner}>
      {@render children()}
    </div>
  </div>
</section>
